import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, MapPin } from 'lucide-react'

import { useBranch } from '@/contexts/BranchContext'
import { cn } from '@/lib/utils'

export function BranchSwitcher() {
  const { branches, activeBranch, setActiveBranch, canSwitchBranch } = useBranch()
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (!activeBranch) {
    return null
  }

  const label = activeBranch.name ?? activeBranch.code

  return (
    <div className="relative hidden shrink-0 xl:block" ref={menuRef}>
      <button
        type="button"
        onClick={() => canSwitchBranch && setOpen((value) => !value)}
        className={cn(
          'flex max-w-[9.5rem] items-center gap-1.5 rounded-2xl bg-muted/50 px-2.5 py-1.5 text-left transition-colors 2xl:max-w-[11rem] 2xl:gap-2 2xl:px-3',
          canSwitchBranch ? 'hover:bg-muted/80' : 'cursor-default',
        )}
        aria-expanded={open}
        aria-haspopup={canSwitchBranch ? 'listbox' : undefined}
        disabled={!canSwitchBranch}
        title={label}
      >
        <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
        <span className="truncate text-[12px] font-medium">{label}</span>
        {canSwitchBranch ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
      </button>

      {open && canSwitchBranch ? (
        <div
          role="listbox"
          aria-label="Select branch"
          className={cn(
            'absolute left-0 top-[calc(100%+0.5rem)] z-50 min-w-[12rem]',
            'rounded-2xl border border-border/60 bg-card p-1.5 shadow-lg',
          )}
        >
          {branches.map((branch) => {
            const isActive = branch.id === activeBranch.id
            return (
              <button
                key={branch.id}
                type="button"
                role="option"
                aria-selected={isActive}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[13px] transition-colors',
                  isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80',
                )}
                onClick={() => {
                  setActiveBranch(branch.id)
                  setOpen(false)
                }}
              >
                <span className="flex-1 truncate">{branch.name}</span>
                {isActive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
